import React, { useState } from 'react';
import { Box, ButtonBase, Divider, Stack, Typography } from '@mui/material';
import { ChevronRight, SchoolOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import type { AdmissionApplication, AdmissionCycle } from '../../../types/admissions';
import { admissionCycleService } from '../../../services/admissions/admissionCycleService';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import CandidateDrawer from '../../admissions/CandidateDrawer';
import { useTimezone } from '../../../hooks/useTimezone';
import { SectionCard } from '../../common/SectionCard';
import StatusChip from '../../common/StatusChip';
import EmptyState from '../../common/EmptyState';
import { placementName } from './enrollmentPlacement';

interface Props {
  admissions: AdmissionApplication[];
  enrollments: any[];
  /** Reload the profile after the applicant drawer changes something. */
  onChanged: () => void;
}

const nameOf = (v: any): string | undefined => (v && typeof v === 'object' ? v.name : undefined);

/** A row in a card list: title + muted line on the left, status on the right. */
const Row: React.FC<{ title: string; meta: string; status: React.ReactNode; onClick?: () => void }> = ({
  title, meta, status, onClick,
}) => {
  const body = (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 1.5, width: '100%', textAlign: 'left' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>{title}</Typography>
        <Typography variant="caption" color="text.secondary">{meta}</Typography>
      </Box>
      {status}
      {onClick && <ChevronRight fontSize="small" sx={{ color: 'text.disabled' }} />}
    </Stack>
  );
  return onClick
    ? <ButtonBase onClick={onClick} sx={{ display: 'block', width: '100%', borderRadius: 1.5, px: 1, mx: -1, '&:hover': { bgcolor: 'action.hover' } }}>{body}</ButtonBase>
    : body;
};

/** The person's school journey — the classes they've been in and every application they've made. */
const StudentAcademicsTab: React.FC<Props> = ({ admissions, enrollments, onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { canRead } = useModulePermissions();
  const { formatDate } = useTimezone();
  const [open, setOpen] = useState<{ applicationId: string; cycle: AdmissionCycle } | null>(null);

  // The applicant drawer opens right here, over the profile.
  const openApplication = async (applicationId: string, cycleId: string) => {
    try {
      setOpen({ applicationId, cycle: await admissionCycleService.get(cycleId) });
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not open the application', { variant: 'error' });
    }
  };

  if (admissions.length === 0 && enrollments.length === 0) {
    return (
      <EmptyState
        icon={<SchoolOutlined />}
        title="No academic record yet"
        description="Applications and class enrollments appear here."
        dense
      />
    );
  }

  return (
    <Stack spacing={2.5}>
      {enrollments.length > 0 && (
        <SectionCard title="Classes">
          <Stack divider={<Divider />} sx={{ mt: -1.5, mb: -1 }}>
            {enrollments.map((e: any) => {
              const from = e.enrolledOn || e.createdAt;
              const span = [from && formatDate(from), e.exitedOn && formatDate(e.exitedOn)].filter(Boolean).join(' – ');
              return (
                <Row
                  key={e._id}
                  title={placementName(e) || 'Class not set'}
                  meta={[e.sessionId?.name, e.rollNumber != null && `Roll ${e.rollNumber}`, span].filter(Boolean).join(' · ')}
                  status={<StatusChip status={{ kind: 'enrollment', value: e.enrollmentStatus }} />}
                />
              );
            })}
          </Stack>
        </SectionCard>
      )}

      {admissions.length > 0 && (
        <SectionCard title="Applications">
          <Stack divider={<Divider />} sx={{ mt: -1.5, mb: -1 }}>
            {admissions.map(app => {
              const cycleId = typeof app.cycleId === 'object' ? app.cycleId._id : app.cycleId;
              const applied = (app as any).submittedAt || app.createdAt;
              const merit = app.computedMeritScore;
              return (
                <Row
                  key={app._id}
                  title={nameOf(app.cycleId) || 'Admission'}
                  meta={[
                    nameOf(app.classId) && `Applied for ${nameOf(app.classId)}`,
                    applied && formatDate(applied),
                    merit != null && `Merit ${merit.toFixed(2)}`,
                  ].filter(Boolean).join(' · ')}
                  status={<StatusChip status={{ kind: 'application', value: app.status }} />}
                  onClick={cycleId && canRead('admissions') ? () => openApplication(app._id, cycleId) : undefined}
                />
              );
            })}
          </Stack>
        </SectionCard>
      )}

      {open && (
        <CandidateDrawer applicationId={open.applicationId} cycle={open.cycle} onClose={() => setOpen(null)} onMutated={onChanged} />
      )}
    </Stack>
  );
};

export default StudentAcademicsTab;
